import { type PropsWithChildren, type ReactElement, useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { Stack, Switch, Text, Title } from '@mantine/core'
import { useTerrosMcpFeature } from '@hooks'
import { getMcpUrl, useBackend, useTranslation } from '@front/model'
import { CopyText, NotReady, Page, TButton } from '@components'
import { claudeCodeCommand, cursorInstallLink, MCP_DOCS_URL, vsCodeInstallLink } from './aiClientLinks'

function TerrosMcp(): ReactElement {
  const { t } = useTranslation()
  const { isEnabled, isLoading } = useTerrosMcpFeature()
  if (isLoading) return <NotReady isLoading />
  if (!isEnabled) return <NotReady error={t('TerrosMCP is not available')} />
  return <TerrosMcpPage />
}

function TerrosMcpPage(): ReactElement {
  const { t } = useTranslation()
  const { getStage } = useBackend()
  const [allowWrites, setAllowWrites] = useState(false)
  const baseUrl = getMcpUrl(getStage())
  const mcpUrl = allowWrites ? `${baseUrl}?writes=enabled` : baseUrl

  return (
    <Page>
      <Page.Heading title={t('TerrosMCP')}>
        <TButton component='a' href={MCP_DOCS_URL} target='_blank' icon='open_in_new' variant='secondary'>
          {t('Setup guide')}
        </TButton>
      </Page.Heading>
      <Page.Content scrollArea>
        <Stack gap='lg' maw={720}>
          <Text>
            {t(
              'Connect Claude, ChatGPT, Cursor, VS Code or another MCP client to Terros. Add this address to your AI client and sign in with your Terros account when it asks. The AI can see and do only what your Terros role allows.'
            )}
          </Text>
          <CopyText label={t('MCP server address')} value={mcpUrl} />
          <Switch
            checked={allowWrites}
            label={t('Allow changes')}
            description={t(
              'Off: the AI can only read Terros data. On: it can also change data as you, so review each change it makes.'
            )}
            onChange={(e) => setAllowWrites(e.currentTarget.checked)}
          />
          <ClientSection title='Claude Code'>
            <CopyText
              label={t('Run this in a terminal, then run /mcp in Claude Code to sign in')}
              value={claudeCodeCommand(mcpUrl)}
            />
          </ClientSection>
          <ClientSection title='Claude'>
            <Text size='sm'>{t('In Settings → Connectors, add a custom connector with the address above.')}</Text>
            <ExternalLink href='https://claude.ai/settings/connectors'>{t('Open Claude connectors')}</ExternalLink>
          </ClientSection>
          <ClientSection title='ChatGPT'>
            <Text size='sm'>
              {t("Needs ChatGPT's own developer mode. Create an app with the address above and OAuth sign-in.")}
            </Text>
            <ExternalLink href={`${MCP_DOCS_URL}#chatgpt`}>{t('ChatGPT steps')}</ExternalLink>
          </ClientSection>
          <ClientSection title='Cursor'>
            <InstallLink href={cursorInstallLink(mcpUrl)}>{t('Add to Cursor')}</InstallLink>
          </ClientSection>
          <ClientSection title='VS Code'>
            <InstallLink href={vsCodeInstallLink(mcpUrl)}>{t('Add to VS Code')}</InstallLink>
          </ClientSection>
          <Text size='sm' c='dimmed'>
            {t(
              "These steps follow each AI client's own documentation and have not yet been tested with Terros. See the setup guide for details."
            )}
          </Text>
        </Stack>
      </Page.Content>
    </Page>
  )
}

const ClientSection = ({ title, children }: PropsWithChildren<{ title: string }>): ReactElement => (
  <Stack gap='xs' align='flex-start'>
    <Title order={5}>{title}</Title>
    {children}
  </Stack>
)

const ExternalLink = ({ href, children }: PropsWithChildren<{ href: string }>): ReactElement => (
  <TButton component='a' href={href} target='_blank' icon='open_in_new' variant='secondary'>
    {children}
  </TButton>
)

// The app opens from its own URL scheme, so this link stays in the current tab instead of opening a blank one.
const InstallLink = ({ href, children }: PropsWithChildren<{ href: string }>): ReactElement => (
  <TButton component='a' href={href} icon='download' variant='secondary'>
    {children}
  </TButton>
)

export const Route = createFileRoute('/integration/mcp/')({
  component: TerrosMcp,
})
